import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShoppingBag, Play, Power, RefreshCw, Film, Wand2 } from "lucide-react";
import { avenoEngineSync, avenoEngineStatus, avenoEngineToggle, avenoEngineRunNext, avenoEngineRefreshAll } from "@/lib/api";

const statusChip = (s) => {
  const map = {
    done: "bg-green-100 text-green-800",
    pending: "bg-amber-100 text-amber-800",
    processing: "bg-blue-100 text-blue-800",
    error: "bg-red-100 text-red-800",
  };
  return `px-2 py-0.5 text-xs font-mono uppercase tracking-wide ${map[s] || "bg-secondary text-muted-foreground"}`;
};

export default function AdminAvenoEngine() {
  const [st, setSt] = useState(null);
  const [perDay, setPerDay] = useState(7);
  const [regenVideos, setRegenVideos] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    avenoEngineStatus().then((d) => { setSt(d); setPerDay(d.per_day || 7); }).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); if (r && r.ok === false) toast.error(r.error || "Failed"); else toast.success(okMsg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  if (!st) return <p className="text-muted-foreground mt-10">Loading AVENO engine…</p>;

  return (
    <div data-testid="aveno-engine" className="mt-12 border-t border-border pt-10">
      <div className="flex items-center gap-3 mb-2">
        <ShoppingBag className="w-7 h-7 text-primary" />
        <h2 className="font-display text-3xl">AVENO Content Engine</h2>
      </div>
      <p className="text-muted-foreground mb-8 max-w-2xl">
        For every product in your AVENO store, the engine writes a story-driven blog post (with photos and a buy link)
        and creates two cinematic ad videos (vertical + landscape, from the product photo). Posts publish on a daily
        pace and auto-share to your connected social networks. Videos are generated with AI and billed to your
        Universal Key balance — roughly a dollar or less per product.
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-display text-xl">Engine</p>
              <p className="text-sm text-muted-foreground">Releases products at a steady daily pace.</p>
            </div>
            <button data-testid="aveno-engine-toggle"
              onClick={() => run("toggle", () => avenoEngineToggle(!st.enabled, perDay), st.enabled ? "Engine paused" : "Engine running")}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition ${st.enabled ? "border-green-600 text-green-700" : "border-border text-muted-foreground"}`}>
              <Power className="w-4 h-4" /> {st.enabled ? "On" : "Off"}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <label className="overline text-muted-foreground">Products per day</label>
            <select data-testid="aveno-per-day" value={perDay} onChange={(e) => setPerDay(Number(e.target.value))}
              className="bg-secondary px-3 py-2 text-sm outline-none" disabled={st.enabled}>
              {[3, 5, 7, 10].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            {st.enabled && <span className="text-xs text-muted-foreground">pause the engine to change pace</span>}
          </div>
          <div className="flex flex-wrap gap-3">
            <button data-testid="aveno-sync-btn" disabled={busy === "sync"}
              onClick={() => run("sync", avenoEngineSync, "Store synced into the queue")}
              className="inline-flex items-center gap-2 bg-secondary px-4 py-2.5 text-sm font-semibold hover:bg-secondary/70 transition disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${busy === "sync" ? "animate-spin" : ""}`} /> Sync products from store
            </button>
            <button data-testid="aveno-run-next-btn" disabled={busy === "run"}
              onClick={() => { if (window.confirm("Process the next product now? It writes the blog post and generates both videos (takes a few minutes).")) run("run", avenoEngineRunNext, "Started — check back in a few minutes"); }}
              className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-50">
              <Play className="w-4 h-4" /> Process next product now
            </button>
          </div>
          <div className="border-t border-border pt-4 space-y-3">
            <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="aveno-regen-videos-label">
              <input type="checkbox" data-testid="aveno-regen-videos" checked={regenVideos} onChange={(e) => setRegenVideos(e.target.checked)} className="mt-1" />
              <span className="text-muted-foreground">Also regenerate product videos in the new <strong className="text-foreground">lifestyle style</strong> — products shown in use, modeled by people (~$1/product to Universal Key). Uncheck to keep existing videos.</span>
            </label>
            <button data-testid="aveno-refresh-all-btn" disabled={busy === "refresh" || !(st.counts?.done > 0)}
              onClick={() => {
                if (window.confirm(
                  `Rebuild all ${st.counts?.done ?? 0} existing AVENO posts to the latest spec?\n\n` +
                  "• Blog posts rewritten: real product photo first + buy links\n" +
                  "• Social captions link straight to the product on your AVENO store — no bare video URLs\n" +
                  (regenVideos
                    ? "• Product videos REGENERATED in the new lifestyle 'in use / modeled' style (~$1/product)\n"
                    : "• Existing product videos are KEPT (no re-billing for those)\n") +
                  "• Old social posts in the queue are discarded and re-created with the real product photo\n" +
                  "• Product posts only publish once their video exists — the worker retries rather than posting without one\n\n" +
                  "Runs in the background and re-shares at your daily pace."
                )) run("refresh", () => avenoEngineRefreshAll(perDay, regenVideos), "Refresh started — posts rebuild in the background");
              }}
              className="inline-flex items-center gap-2 border border-primary text-primary px-4 py-2.5 text-sm font-semibold hover:bg-primary hover:text-white transition disabled:opacity-50">
              <Wand2 className={`w-4 h-4 ${busy === "refresh" ? "animate-pulse" : ""}`} /> Refresh all posts to latest spec
            </button>
            <p className="text-xs text-muted-foreground mt-2">
              Use this after spec changes: it rebuilds posts that were already published with the old format and
              re-queues their social shares with the actual product photo instead of AI illustrations.
            </p>
          </div>
        </div>

        <div className="border border-border bg-card p-6">
          <p className="font-display text-xl mb-4">Queue</p>
          <div className="grid grid-cols-4 gap-3 text-center mb-4">
            {[["Pending", "pending"], ["Working", "processing"], ["Live", "done"], ["Failed", "error"]].map(([label, k]) => (
              <div key={k} className="bg-secondary/50 py-3">
                <p className="font-display text-2xl" data-testid={`aveno-count-${k}`}>{st.counts?.[k] ?? 0}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground" data-testid="aveno-today">
            Today: <strong>{st.today?.published ?? 0}</strong> / {st.per_day} products published
          </p>
        </div>
      </div>

      <div className="border border-border bg-card p-6 mt-6">
        <p className="font-display text-xl mb-4">Latest activity</p>
        {(!st.recent || st.recent.length === 0) && <p className="text-sm text-muted-foreground" data-testid="aveno-recent-empty">Nothing yet — sync the store, then run one product or switch the engine on.</p>}
        <div className="space-y-2">
          {(st.recent || []).map((r, i) => (
            <div key={r.handle || i} data-testid={`aveno-item-${i}`} className="flex items-center justify-between gap-3 text-sm border-b border-border pb-2 last:border-0">
              <span className="truncate font-medium">{r.title || r.handle}</span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                {r.has_videos && <span className="inline-flex items-center gap-1"><Film className="w-3 h-3" /> video</span>}
                {r.blog_slug && <a href={`/blog/${r.blog_slug}`} target="_blank" rel="noreferrer" className="text-primary underline">post</a>}
                {r.error && <span className="text-red-600 truncate max-w-[200px]" title={r.error}>{r.error}</span>}
                <span className={statusChip(r.status)}>{r.status}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
